// src/pages/LaserCutInterlocking.js
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { ADDITION, SUBTRACTION, Brush, Evaluator } from "three-bvh-csg";
import {
  processIndices,
  computeMeshIntersection,
} from "../utils/intersectionHelpers";
import { createCachedMaterialsLaserCutInterlocking } from "../utils/materialManager";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import { handleSeparateExport } from "../utils/separateSVGExport";
import useVisualHelpers from "../hooks/useVisualHelpers";
import TutorialWindow from "../components/TutorialWindow";
import { laserCutInterlockingTutorial } from "../utils/tutorialContents";
import { useSyncedCamera } from "../hooks/useSyncedCamera";
import { normalizeModel } from "../utils/normalizeModel";

import { hull } from "../utils/hull";
import "../styles/common.css";

const setupWorker = () => {
  return {
    postMessage: (data) => {
      //    //console.log("Processing in worker:", data);
    },
    onmessage: null,
  };
};

// Main component
const LaserCutInterlocking = ({ stlFile }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const { createVisualization, removeVisualHelpers } = useVisualHelpers();
  const workerRef = useRef(null);
  const helpersGroupRef = useRef(null);
  const gridHelperRef = useRef(null);
  const [showControlPanel, setShowControlPanel] = useState(false);

  // State variables
  const [cutNumber, setCutNumber] = useState(6);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [svgReady, setSvgReady] = useState(false);
  const [modelDimensions, setModelDimensions] = useState(null);
  const [materialThickness, setMaterialThickness] = useState(3);
  const [expanded, setExpanded] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const [tutorialVisible, setTutorialVisible] = useState(false);
  const lineGroupsRef = useRef([]);

  // 生成唯一页面ID
  const pageId = useMemo(() => `laser-cut`, []);

  // 使用相机同步钩子
  useSyncedCamera(pageId, cameraRef, controlsRef);

  // Object references
  const objRef = useRef(null);
  const horizontalSlicesRef = useRef([]);
  const verticalSlicesRef = useRef([]);
  const horizontalContoursRef = useRef([]);
  const verticalContoursRef = useRef([]);
  const xpositiveRef = useRef([]);
  const xnegRef = useRef([]);

  // Cached materials for reuse
  const cachedMaterials = useMemo(
    () => createCachedMaterialsLaserCutInterlocking(),
    []
  );

  const toggleTutorial = useCallback(() => {
    setTutorialVisible((prev) => !prev);
  }, []);

  const evaluator = useMemo(() => new Evaluator(), []);

  // Initialize grid and axes helpers
  const initHelpers = useCallback(() => {
    if (!sceneRef.current) return;

    if (!gridHelperRef.current) {
      const gridHelper = new THREE.GridHelper(200, 20, 0x888888, 0xaaaaaa);

      gridHelper.position.y = -0.5;

      gridHelper.material.opacity = 0.5;
      gridHelper.material.transparent = true;

      sceneRef.current.add(gridHelper);
      gridHelperRef.current = gridHelper;

      const axesHelper = new THREE.AxesHelper(100);
      sceneRef.current.add(axesHelper);

      //    //console.log("Grid and axes helpers initialized");
    }
  }, []);

  // Throttle operations with requestAnimationFrame/setTimeout
  const throttle = useCallback((fn, delay = 0) => {
    let lastCall = 0;
    return function (...args) {
      const now = new Date().getTime();
      if (now - lastCall < delay) {
        return;
      }
      lastCall = now;
      return fn(...args);
    };
  }, []);

  // Reuse geometries and materials
  const mergeMeshes = useCallback((evaluator, meshes, operation, material) => {
    if (meshes.length === 0) return null;
    let result = meshes[0];
    for (let i = 1, l = meshes.length; i < l; i++) {
      result = evaluator.evaluate(result, meshes[i], operation);
      result.material = material;
    }
    return result;
  }, []);

  const createFinalMesh = useCallback((geometry, material, referenceMesh) => {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(referenceMesh.position);
    mesh.rotation.copy(referenceMesh.rotation);
    mesh.scale.copy(referenceMesh.scale);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.updateMatrixWorld(true);
    return mesh;
  }, []);

  // Process horizontal intersections
  // Fixed processHorizontalIntersection function:
  const processHorizontalIntersection = useCallback(
    (horizontalContours, horizontalMeshThick, intersectionPoints, index) => {
      if (
        intersectionPoints.length > 3 &&
        intersectionPoints[0].hasOwnProperty("x") &&
        intersectionPoints[0].hasOwnProperty("z")
      ) {
        // Convert points to 2D for hull calculation
        const points2D = intersectionPoints.map((p) => [p.x, p.z]);

        // Use all points directly instead of simplifying
        const hullPoints = hull(points2D, 30);

        if (hullPoints && hullPoints.length > 2) {
          const shape = new THREE.Shape();

          hullPoints.forEach((point, i) => {
            if (i === 0) {
              shape.moveTo(point[0], point[1]);
            } else {
              shape.lineTo(point[0], point[1]);
            }
          });
          shape.closePath();

          // Calculate average Y position
          const averageY =
            intersectionPoints.reduce((sum, p) => sum + p.y, 0) /
            intersectionPoints.length;

          // Create extrusion settings
          const extrudeSettings = {
            depth: materialThickness,
            bevelEnabled: false,
            curveSegments: 8,
            steps: 1,
          };

          // Create extruded geometry
          const extrudeGeometry = new THREE.ExtrudeGeometry(
            shape,
            extrudeSettings
          );
          const material = cachedMaterials.hplaneMaterial;

          // Create brush for CSG
          const brush = new Brush(extrudeGeometry, material);
          brush.position.y = averageY - materialThickness / 2;
          brush.rotation.x = Math.PI / 2;
          brush.updateMatrixWorld();
          horizontalMeshThick.push(brush);

          // Store contour for SVG export
          horizontalContours[index] = horizontalContours[index] || [];
          horizontalContours[index].push({
            points: shape.getPoints(),
            position: new THREE.Vector3(0, averageY, 0),
            rotation: new THREE.Euler(Math.PI / 2, 0, 0),
          });
        }
      }
    },
    [materialThickness, cachedMaterials]
  );

  // Fixed processVerticalIntersection function:
  const processVerticalIntersection = useCallback(
    (
      verticalContours,
      verticalMeshThick,
      intersectionPoints,
      index,
      verticalShapes
    ) => {
      // Convert points to 2D for hull calculation
      const points2D = intersectionPoints.map((p) => [p.x, p.y]);

      // Use all points directly instead of simplifying
      const hullPoints = hull(points2D, 30);

      if (hullPoints && hullPoints.length > 2) {
        const shape = new THREE.Shape();

        hullPoints.forEach((point, i) => {
          if (i === 0) {
            shape.moveTo(point[0], point[1]);
          } else {
            shape.lineTo(point[0], point[1]);
          }
        });
        shape.closePath();

        // Calculate average Z position
        const averageZ =
          intersectionPoints.reduce((sum, p) => sum + p.z, 0) /
          intersectionPoints.length;

        // Create shape geometry for intersection calculations
        const shapeGeometry = new THREE.ShapeGeometry(shape, 8);
        const meshMaterial = new THREE.MeshBasicMaterial({
          color: 0xffff00,
          side: THREE.DoubleSide,
          visible: false,
        });

        const verticalMesh = new THREE.Mesh(shapeGeometry, meshMaterial);
        verticalMesh.position.z = averageZ;
        verticalShapes.push(verticalMesh);

        // Create extrusion settings
        const extrudeSettings = {
          depth: materialThickness,
          bevelEnabled: false,
          curveSegments: 8,
          steps: 1,
        };

        // Create extruded geometry
        const extrudeGeometry = new THREE.ExtrudeGeometry(
          shape,
          extrudeSettings
        );

        // Create brush for CSG
        const thickVb = new Brush(
          extrudeGeometry,
          cachedMaterials.vplaneMaterial
        );
        thickVb.position.z = averageZ - materialThickness / 2;
        thickVb.updateMatrixWorld();
        verticalMeshThick.push(thickVb);

        // Store for later processing
        verticalContours[index] = verticalContours[index] || [];
        verticalContours[index].push({
          points: shape.getPoints(),
          position: new THREE.Vector3(0, 0, averageZ),
          originalZ: averageZ,
        });
      }
    },
    [materialThickness, cachedMaterials]
  );

  // Process a direction for interlocking
  const processDirection = useCallback(
    (
      midPoint,
      size,
      planeHeight,
      direction,
      container,
      boxContainer,
      verticalPlaneIndex,
      horizontalPlaneIndex
    ) => {
      const rectangleCurves = new THREE.Group();

      // Process slots - adjusted for proper alignment
      const slotWidth = materialThickness * 1.03;
      const slotHeight = materialThickness * 1.03;

      // Position new midpoint with offsets
      const newMidPoint = midPoint.clone();
      newMidPoint.z += slotWidth / 2;
      newMidPoint.y += -slotHeight / 2 + (direction === 1 ? 0.1 : -0.1);

      const newExtensionEndPoint = new THREE.Vector3(
        midPoint.x + direction * size.x * 0.6,
        newMidPoint.y,
        newMidPoint.z
      );

      // Calculate line properties
      const lineLength = new THREE.Vector3()
        .subVectors(newExtensionEndPoint, newMidPoint)
        .length();
      const lineMidPoint = new THREE.Vector3()
        .addVectors(newMidPoint, newExtensionEndPoint)
        .multiplyScalar(0.5);
      lineMidPoint.z -= 0.5 * materialThickness;
      lineMidPoint.y -= 0.5 * materialThickness;

      // Create slot box
      const boxGeometry = new THREE.BoxGeometry(
        lineLength,
        slotHeight,
        slotWidth
      );

      // Create brush for CSG
      const material =
        direction === 1
          ? cachedMaterials.blueBoxMaterial
          : cachedMaterials.greenBoxMaterial;
      const brush = new Brush(boxGeometry, material);
      brush.position.copy(lineMidPoint);
      brush.updateMatrixWorld(true);
      boxContainer.push(brush);

      // 在创建brush后添加线条
      const lineStart = midPoint.clone();
      const lineEnd = new THREE.Vector3(
        midPoint.x + direction * size.x * 0.6,
        midPoint.y,
        midPoint.z
      );

      const lineGeometry = new THREE.BufferGeometry().setFromPoints([
        lineStart,
        lineEnd,
      ]);
      const lineMaterial = new THREE.LineBasicMaterial({
        color: direction === 1 ? 0xff0000 : 0x00ff00,
        linewidth: 2,
      });
      const slotLine = new THREE.Line(lineGeometry, lineMaterial);

      // 把线条添加到容器中
      rectangleCurves.add(slotLine);

      // 创建一个组来包含这条线，并记录它属于哪个平面
      const lineGroup = new THREE.Group();
      lineGroup.add(slotLine);

      // 添加平面索引信息到userData
      lineGroup.userData = {
        verticalPlaneIndex: verticalPlaneIndex,
        horizontalPlaneIndex: horizontalPlaneIndex,
      };

      if (direction === 1) {
        xpositiveRef.current.push(lineGroup);
      } else {
        xnegRef.current.push(lineGroup);
      }

      container.push(rectangleCurves);
    },
    [materialThickness, cachedMaterials]
  );

  // Process baseline to create interlocking slots
  const processBaselineDirections = useCallback(
    (
      baseline,
      size,
      planeHeight,
      xpositive,
      xneg,
      bluebox,
      greenbox,
      verticalShapeIndices
    ) => {
      if (!baseline || !baseline.isGroup) {
        //    //console.error("Invalid baseline:", baseline);
        return;
      }

      baseline.children.forEach((child, childIndex) => {
        if (!(child instanceof THREE.Line)) {
          return;
        }

        const geometry = child.geometry;
        const positions = geometry.attributes.position;

        if (positions.count < 2) {
          return;
        }

        // 获取这条baseline对应的垂直和水平平面索引
        const verticalPlaneIndex = child.userData.verticalPlaneIndex;
        const horizontalPlaneIndex = child.userData.horizontalPlaneIndex;

        baseline.updateMatrixWorld(true);

        // Calculate the midpoint
        const startPoint = new THREE.Vector3();
        const endPoint = new THREE.Vector3();

        startPoint.fromBufferAttribute(positions, 0);
        endPoint.fromBufferAttribute(positions, positions.count - 1);

        startPoint.applyMatrix4(child.matrixWorld);
        endPoint.applyMatrix4(child.matrixWorld);

        const midPoint = new THREE.Vector3()
          .addVectors(startPoint, endPoint)
          .multiplyScalar(0.5);

        // Process in both directions with plane indices
        processDirection(
          midPoint.clone(),
          size,
          planeHeight,
          1,
          xpositive,
          bluebox,
          verticalPlaneIndex,
          horizontalPlaneIndex
        );
        processDirection(
          midPoint.clone(),
          size,
          planeHeight,
          -1,
          xneg,
          greenbox,
          verticalPlaneIndex,
          horizontalPlaneIndex
        );
      });
    },
    [processDirection]
  );

  // Clear previous objects
  const clearPreviousObjects = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    // Preserve the grid helper while clearing previous objects
    if (gridHelperRef.current) {
      scene.remove(gridHelperRef.current);
      gridHelperRef.current = null;
    }

    if (helpersGroupRef.current) {
      removeVisualHelpers(scene);
      helpersGroupRef.current = null;
    }

    // Helper function to remove and dispose objects
    const cleanupObjects = (objects) => {
      objects.forEach((obj) => {
        if (obj !== gridHelperRef.current) {
          // Ensure grid isn't removed
          scene.remove(obj);
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) {
            if (Array.isArray(obj.material)) {
              obj.material.forEach((m) => m.dispose());
            } else {
              obj.material.dispose();
            }
          }
        }
      });
      return [];
    };

    // Clear all reference arrays, making sure to not remove the grid
    horizontalSlicesRef.current = cleanupObjects(horizontalSlicesRef.current);
    verticalSlicesRef.current = cleanupObjects(verticalSlicesRef.current);

    // Reset other arrays
    horizontalContoursRef.current = [];
    verticalContoursRef.current = [];
    xpositiveRef.current = [];
    xnegRef.current = [];
    lineGroupsRef.current = []; // 添加这一行
  }, [removeVisualHelpers]);

  const createHelpers = useCallback(() => {
    if (!sceneRef.current) return;

    // Remove existing helpers
    if (helpersGroupRef.current) {
      removeVisualHelpers(sceneRef.current);
      helpersGroupRef.current = null;
    }

    const combinedGroup = new THREE.Group();

    // Add all horizontal and vertical slices to this group
    horizontalSlicesRef.current.forEach((slice) => {
      // Create a clone to avoid modifying original
      const clonedSlice = slice.clone();
      combinedGroup.add(clonedSlice);
    });

    verticalSlicesRef.current.forEach((slice) => {
      // Create a clone to avoid modifying original
      const clonedSlice = slice.clone();
      combinedGroup.add(clonedSlice);
    });

    // If there are no slices, return
    if (combinedGroup.children.length === 0 && !objRef.current) return;

    // Calculate bounding box from the combined group
    const boundingBox = new THREE.Box3().setFromObject(
      combinedGroup.children.length > 0 ? combinedGroup : objRef.current
    );

    // Create helpers based on the bounding box
    //    //console.log("Creating visual helpers with object:", combinedGroup);
    helpersGroupRef.current = createVisualization(
      combinedGroup, // Pass the combined group instead of null
      sceneRef.current,
      boundingBox
    );

    // Remove the temporary group (but not its children since they're clones)
    combinedGroup.children.length = 0;
  }, [createVisualization, removeVisualHelpers]);

  // Main processing function
  const processPaperMache = useCallback(() => {
    if (!objRef.current || !sceneRef.current) return;

    // Clear previous objects
    clearPreviousObjects();

    const obj = objRef.current;
    const scene = sceneRef.current;

    // Calculate model dimensions and position
    const boundingBox = new THREE.Box3().setFromObject(obj);
    const size = boundingBox.getSize(new THREE.Vector3());

    // Position object
    obj.position.set(0, size.y / 2, 0);
    obj.updateMatrixWorld(true);

    // Hide the original model completely
    obj.visible = false;

    // Make sure grid helpers are visible
    initHelpers();

    // Initialize arrays
    const Horizontalmeshthick = [];
    const verticalmeshthick = [];
    const greenbox = [];
    const bluebox = [];
    const verticalShape = [];

    const count = cutNumber;
    const planeHeight = size.y / count;

    setProcessingStatus("Processing horizontal planes...");

    setTimeout(() => {
      // Process horizontal planes
      for (let i = 0; i < count; i++) {
        const intersectionPoints = [];
        const vertices = obj.geometry.attributes.position;
        const indices = obj.geometry.index;
        const mathPlane = new THREE.Plane(
          new THREE.Vector3(0, 1, 0),
          -(size.y / count / 2 + i * planeHeight)
        );

        processIndices(indices, vertices, obj, mathPlane, intersectionPoints);

        processHorizontalIntersection(
          horizontalContoursRef.current,
          Horizontalmeshthick,
          intersectionPoints,
          i
        );
      }

      setProcessingStatus("Processing vertical planes...");
      setTimeout(() => {
        // Process vertical planes
        const planestep = size.z / count;

        for (let i = 0; i < count; i++) {
          const intersectionPoints = [];
          const vertices = obj.geometry.attributes.position;
          const indices = obj.geometry.index;

          const mathPlane = new THREE.Plane(
            new THREE.Vector3(0, 0, 1),
            -(boundingBox.min.z + size.z / count / 2 + i * planestep)
          );

          processIndices(indices, vertices, obj, mathPlane, intersectionPoints);

          if (intersectionPoints.length > 3) {
            processVerticalIntersection(
              verticalContoursRef.current,
              verticalmeshthick,
              intersectionPoints,
              i,
              verticalShape
            );
          }
        }

        setProcessingStatus("Generating interlocking slots...");
        setTimeout(() => {
          // Generate baseline
          const baseline = new THREE.Group();

          for (let i = 0; i < count; i++) {
            const mathPlaneH = new THREE.Plane(
              new THREE.Vector3(0, 1, 0),
              -(size.y / count / 2 + i * planeHeight)
            );

            verticalShape.forEach((vMesh, vIndex) => {
              const intersectionPoints = [];
              const vertices = vMesh.geometry.attributes.position;
              const indices = vMesh.geometry.index;

              computeMeshIntersection(
                indices,
                vertices,
                vMesh,
                mathPlaneH,
                intersectionPoints
              );

              if (intersectionPoints.length > 0) {
                const lineGeometry = new THREE.BufferGeometry().setFromPoints(
                  intersectionPoints
                );
                const intersectionLine = new THREE.Line(
                  lineGeometry,
                  new THREE.LineBasicMaterial({
                    color: 0xff0000,
                    visible: false,
                  })
                );

                // 记录这条intersection line属于哪个垂直平面和水平平面
                intersectionLine.userData = {
                  verticalPlaneIndex: vIndex,
                  horizontalPlaneIndex: i,
                };

                baseline.add(intersectionLine);
              }
            });
          }

          // 在调用 processBaselineDirections 时，不需要额外参数了
          processBaselineDirections(
            baseline,
            size,
            planeHeight,
            xpositiveRef.current,
            xnegRef.current,
            bluebox,
            greenbox
          );

          // 组织xneg中的线条到lineGroups中
          const cutNumber = count; // 使用当前的count值
          for (let i = 0; i < cutNumber; i++) {
            lineGroupsRef.current[i] = lineGroupsRef.current[i] || [];
            xnegRef.current.forEach((group, index) => {
              if (index % cutNumber === i && index < cutNumber * cutNumber) {
                if (group.children) {
                  group.children.forEach((child) => {
                    lineGroupsRef.current[i].push(child);
                  });
                }
              }
            });
          }

          setProcessingStatus("Finalizing model...");
          setTimeout(() => {
            const woodMaterial = cachedMaterials.woodMaterial;

            if (Horizontalmeshthick.length > 0 && greenbox.length > 0) {
              let Hplanes = mergeMeshes(
                evaluator,
                Horizontalmeshthick,
                ADDITION,
                cachedMaterials.hplaneMaterial
              );
              let firstHalf = mergeMeshes(
                evaluator,
                greenbox,
                ADDITION,
                cachedMaterials.greenBoxMaterial
              );

              if (Hplanes && firstHalf) {
                const Fhalf = evaluator.evaluate(
                  Hplanes,
                  firstHalf,
                  SUBTRACTION
                );
                Fhalf.position.copy(Hplanes.position);
                Fhalf.rotation.copy(Hplanes.rotation);
                Fhalf.scale.copy(Hplanes.scale);
                Fhalf.updateMatrixWorld();
                const FhalfMesh = createFinalMesh(
                  Fhalf.geometry,
                  woodMaterial,
                  Fhalf
                );
                horizontalSlicesRef.current.push(FhalfMesh);
                scene.add(FhalfMesh);
              }
            }

            // Process Vertical Planes with CSG
            if (verticalmeshthick.length > 0 && bluebox.length > 0) {
              let Vplanes = mergeMeshes(
                evaluator,
                verticalmeshthick,
                ADDITION,
                cachedMaterials.vplaneMaterial
              );
              let secondHalf = mergeMeshes(
                evaluator,
                bluebox,
                ADDITION,
                cachedMaterials.blueBoxMaterial
              );

              if (Vplanes && secondHalf) {
                const Shalf = evaluator.evaluate(
                  Vplanes,
                  secondHalf,
                  SUBTRACTION
                );
                Shalf.position.copy(Vplanes.position);
                Shalf.rotation.copy(Vplanes.rotation);
                Shalf.scale.copy(Vplanes.scale);
                Shalf.updateMatrixWorld();
                const ShalfMesh = createFinalMesh(
                  Shalf.geometry,
                  woodMaterial,
                  Shalf
                );
                verticalSlicesRef.current.push(ShalfMesh);
                scene.add(ShalfMesh);
              }
            }

            createHelpers();

            setProcessingStatus("");
            setSvgReady(true);
          }, 50);
        }, 50);
      }, 50);
    }, 50);
  }, [
    clearPreviousObjects,
    cutNumber,
    evaluator,
    materialThickness,
    processBaselineDirections,
    processHorizontalIntersection,
    processVerticalIntersection,
    cachedMaterials,
    createFinalMesh,
    mergeMeshes,
    createHelpers,
    initHelpers,
  ]);

  const loadModel = useCallback(() => {
    setIsLoading(true);
    setError(null);
    setSvgReady(false);

    // Cleanup any existing model
    if (objRef.current) {
      sceneRef.current?.remove(objRef.current);
      objRef.current.geometry?.dispose();
      if (Array.isArray(objRef.current.material)) {
        objRef.current.material.forEach((m) => m.dispose());
      } else {
        objRef.current.material?.dispose();
      }
      objRef.current = null;
    }

    initHelpers();

    const loader = new STLLoader();

    const modelPath = stlFile || "/models/twistT.stl";
    //    //console.log("Loading model from:", modelPath);

    loader.load(
      modelPath,
      (geometry) => {
        const material = cachedMaterials.originalModelMaterial;
        const mesh = new THREE.Mesh(geometry, material);

        normalizeModel(mesh);

        // Set position after matrix update
        const objectBoundingBox = new THREE.Box3().setFromObject(mesh);
        mesh.position.y -= objectBoundingBox.min.y;
        mesh.updateMatrix();
        mesh.updateMatrixWorld(true);

        mesh.castShadow = true;
        mesh.receiveShadow = true;

        // Save dimensions for SVG generation
        const size = new THREE.Vector3();
        objectBoundingBox.getSize(size);
        setModelDimensions({
          width: size.x,
          height: size.y,
          depth: size.z,
        });

        objRef.current = mesh;
        mesh.visible = false; // Hide the original model
        sceneRef.current?.add(mesh);

        setIsLoading(false);

        setTimeout(() => {
          processPaperMache();
        }, 100);
      },
      (xhr) => {
        const percentComplete = (xhr.loaded / xhr.total) * 100;
        setProcessingStatus(`Loading model: ${Math.round(percentComplete)}%`);
      },
      (error) => {
        //    //console.error(`Error loading STL from ${modelPath}:`, error);

        // Create a default cube for testing
        //    //console.log("Creating fallback cube model");
        const geometry = new THREE.BoxGeometry(30, 50, 30);
        const material = cachedMaterials.originalModelMaterial;

        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;

        // Save dimensions for SVG generation
        setModelDimensions({
          width: 30,
          height: 50,
          depth: 30,
        });

        objRef.current = mesh;
        mesh.visible = false; // Hide the original model
        sceneRef.current?.add(mesh);

        setIsLoading(false);

        // Process the model after a short delay
        setTimeout(() => {
          processPaperMache();
        }, 100);
      }
    );
  }, [stlFile, processPaperMache, cachedMaterials, initHelpers]);

  // Handle SVG download
  // 在 handleDownloadSVG 函数中修改：
  const handleDownloadSVG = useCallback(() => {
    //    //console.log("Export SVG button clicked");

    // 显示教程
    setTutorialVisible(true);

    // 调用分离导出函数 - 添加 materialThickness
    handleSeparateExport({
      horizontalContours: horizontalContoursRef.current,
      verticalContours: verticalContoursRef.current,
      xpositive: xpositiveRef.current,
      xneg: xnegRef.current,
      lineGroups: lineGroupsRef.current,
      materialThickness: materialThickness, // 添加这一行
    });
  }, [materialThickness]); // 添加依赖

  const handleCheckFabricable = useCallback(() => {
    const threshold = 50; // mm
    const scene = sceneRef.current;
    const tooSmallShapes = [];

    // 清除旧标记
    scene.children
      .filter((obj) => obj.userData?.fabricCheck)
      .forEach((obj) => scene.remove(obj));

    const checkContours = (contours, type) => {
      contours.forEach((layer, layerIndex) => {
        layer.forEach((contour, contourIndex) => {
          const pts2D = contour.points;
          const position = contour.position || new THREE.Vector3();
          const rotation = contour.rotation || new THREE.Euler();

          if (!pts2D || pts2D.length < 2) return;

          // 1. 转为世界坐标下的 3D 点
          const pts3D = pts2D.map((p) => {
            const v = new THREE.Vector3(p.x, 0, p.y); // 2D shape 点 -> xz 平面
            v.applyEuler(rotation); // 应用 shape 旋转
            v.add(position); // 加上位置偏移
            return v;
          });

          // 2. 累加路径长度（闭合路径）
          let totalLength = 0;
          for (let i = 0; i < pts3D.length - 1; i++) {
            totalLength += pts3D[i].distanceTo(pts3D[i + 1]);
          }
          totalLength += pts3D[pts3D.length - 1].distanceTo(pts3D[0]); // 闭合

          if (totalLength < threshold) {
            tooSmallShapes.push({
              type,
              layerIndex,
              contourIndex,
              length: totalLength.toFixed(2),
              position,
            });

            // 红球标记
            const marker = new THREE.Mesh(
              new THREE.SphereGeometry(1.5, 12, 12),
              new THREE.MeshBasicMaterial({
                color: 0xff0000,
                transparent: true,
                opacity: 0.8,
              })
            );
            marker.position.copy(position);
            marker.userData.fabricCheck = true;
            scene.add(marker);
          }
        });
      });
    };

    checkContours(horizontalContoursRef.current, "Horizontal");
    checkContours(verticalContoursRef.current, "Vertical");

    if (tooSmallShapes.length === 0) {
      alert("✅ All cut shapes are large enough.");
    } else {
      alert(
        `⚠️ Found ${tooSmallShapes.length} cut shape(s) with total edge length < ${threshold}mm.`
      );
      console.warn("Too small shapes:", tooSmallShapes);
    }
  }, []);

  const debouncedProcessPaperMache = useCallback(
    throttle(() => {
      if (objRef.current) {
        processPaperMache();
      }
    }, 300),
    [processPaperMache]
  );

  // Initialize Three.js scene
  useEffect(() => {
    const cleanup = initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );

    // Initialize the worker
    workerRef.current = setupWorker();

    // Load model after scene is initialized
    setTimeout(() => {
      loadModel();
    }, 0);

    return () => {
      workerRef.current = null;

      // Clean up visual helpers before unmounting
      if (sceneRef.current) {
        removeVisualHelpers(sceneRef.current);
      }

      cleanup();
    };
  }, [loadModel, removeVisualHelpers]);

  // Re-load model when stlFile changes
  useEffect(() => {
    if (stlFile && sceneRef.current) {
      //    //console.log("STL file changed to:", stlFile);
      loadModel();
    }
  }, [stlFile, loadModel]);

  // Process model when parameters change
  useEffect(() => {
    debouncedProcessPaperMache();
  }, [cutNumber, materialThickness, debouncedProcessPaperMache]);

  return (
    <div className="container">
      <div ref={containerRef} className="canvas-container"></div>
      <button
        className="panel-toggle-button"
        onClick={() => setShowControlPanel(!showControlPanel)}
      >
        {showControlPanel ? "⟨ Hide Panel" : "⟩ Show Panel"}
      </button>

      {/* Floating Control Panel */}
      {!isLoading && showControlPanel && (
        <div className="controls-panel">
          <div className="panel-title">Laser Cut Interlocking</div>
          {/* Information Section */}
          <div className="panel-section">
            <div className="section-header">
              <h4>Workflow Description</h4>
            </div>
            <div className="section-content">
              <p>
                This workflow converts your 3D model into interlocking slices
                for laser cutting. You can control the number of slices and
                material thickness to suit your fabrication needs.
              </p>
              <p style={{ fontStyle: "italic" }}>
                Recommended for flat sheet materials like plywood or cardboard.
                The interlocking structure ensures strong assembly without glue
                or screws.
              </p>
            </div>
          </div>

          {/* Collapsible Section Toggle */}
          <div
            className="section-toggle"
            onClick={() => setExpanded(!expanded)}
            style={{ cursor: "pointer" }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                marginRight: "5px",
                transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.3s ease",
              }}
            >
              <path d="M8 12L2 6H14L8 12Z" fill="#333" />
            </svg>
            laser_cut_settings
          </div>

          {/* Panel content - conditionally rendered based on expanded state */}
          {expanded && (
            <div className="panel-content">
              <div className="input-group">
                <label>Number of Slices</label>
                <div className="value-display">
                  <input
                    type="range"
                    className="slider"
                    min="3"
                    max="10"
                    value={cutNumber}
                    onChange={(e) => setCutNumber(parseInt(e.target.value))}
                    disabled={isLoading}
                  />
                  <div className="value">{cutNumber}</div>
                </div>
              </div>

              <div className="input-group">
                <label>Material Thickness (mm)</label>
                <div className="value-display">
                  <input
                    type="range"
                    className="slider"
                    min="1"
                    max="5"
                    step="0.5"
                    value={materialThickness}
                    onChange={(e) =>
                      setMaterialThickness(parseFloat(e.target.value))
                    }
                    disabled={isLoading}
                  />
                  <div className="value">{materialThickness}</div>
                </div>
              </div>
            </div>
          )}

          {/* Buttons outside the collapsible section */}
          <div className="buttons-container">
            <button
              onClick={handleDownloadSVG}
              className="action-button"
              disabled={!svgReady || isLoading}
            >
              DOWNLOAD SVG FILES
            </button>
            <button onClick={toggleTutorial} className="action-button">
              {tutorialVisible
                ? "HIDE INTERACTIVE GUIDANCE"
                : "SHOW INTERACTIVE GUIDANCE"}
            </button>

            <button
              onClick={handleCheckFabricable}
              className="action-button"
              disabled={isLoading}
            >
              CHECK FABRICABLE
            </button>
          </div>
        </div>
      )}

      {/* Loading Message */}
      {isLoading && <div className="loading-box">Loading model...</div>}

      {/* Error Message */}
      {error && <div className="error-box">Error: {error}</div>}

      <TutorialWindow
        visible={tutorialVisible}
        onToggleVisibility={toggleTutorial}
        tutorialContent={laserCutInterlockingTutorial}
      />
    </div>
  );
};

export default LaserCutInterlocking;
